import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import {FormArray, FormGroup} from "@angular/forms";
import {PortvisitUtils} from "../../../../refdata/portvisit-utils";
import {
  CopyDataConfirmedEvent,
  HarbourDuesBulkCargoType,
  HarbourDuesCargoType,
  HarbourDuesContainerCargoType,
  HarbourDuesGoodsItemForm,
  UnitOfMass
} from "../../types";
import {HarbourDuesService} from "../../services/harbour-dues.service";
import {VisitContext} from "../../../visit-context";
import {openConfirmationModalWithCallback} from "../../../../common/utils";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../../../common/modal/modal-confirm.component";
import {HarbourDuesCargoService} from '../../services/harbour-dues-cargo.service';
import {HarbourDuesGoodsItem} from 'typescript-models/common';

@Component({
  selector: 'app-cargo-list-amsterdam',
  templateUrl: './cargo-list-amsterdam.component.html',
  styleUrls: ['./cargo-list-amsterdam.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CargoListAmsterdamComponent implements OnInit, OnDestroy {

  @Input() list: FormArray<FormGroup<HarbourDuesGoodsItemForm>>;

  @Input() type: string;

  @Input() index: number;

  @Input() terminalCode: string;

  @Output() copyDataConfirmed = new EventEmitter<CopyDataConfirmedEvent>();

  protected readonly context = VisitContext;

  protected readonly PortvisitUtils = PortvisitUtils;

  protected readonly UnitOfMass = UnitOfMass;

  private service = inject(HarbourDuesService);
  private cargoService = inject(HarbourDuesCargoService);

  ngOnInit(): void {
    this.list.enable();
    this.addDefaultGoodsItem();
  }

  ngOnDestroy() {
    this.list.disable();
  }

  private addDefaultGoodsItem() {
    if (this.list.length < 1) {
      this.addGoodsItem();
    }
  }

  deleteGoodsItem(index: number) {
    this.list.removeAt(index);
  }

  addGoodsItem() {
    this.list.push(this.service.newGoodsItem());
  }

  toggleGoodsCodeType(index: number) {
    this.list.at(index).controls.goodsClassification.reset();
    this.list.at(index).controls.dangerInformation.reset();
  }

  goodsCodeTypeIsUndg(index: number) {
    return this.list.at(index).controls.goodsCodeType.value == 'UNDG';
  }

  combinedWeightExceedsVesselDeadWeightKg() {
    return this.cargoService.isGoodsExceedingWeight(
      this.list.value as HarbourDuesGoodsItem[],
      this.context.getVesselDeadWeightKg()
    );
  }

  copyData() {
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed === null) {
        return;
      }
      if (confirmed) {
        this.copyDataConfirmed.emit({terminalCode: this.terminalCode, index: this.index, type: this.type});
      }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "danger",
      title: "Copy data from other PCS services",
      message: "Data from other PCS services will be copied. Existing entries will be overwritten.",
      messageSeperated: true,
      confirmText: "Copy data",
      cancelText: "Cancel",
    }, 'static')

  }

  cargoTypeIsContainer(index: number) {
    const list = Array.from(Object.values(HarbourDuesContainerCargoType)) as string[];

    return list.includes(this.list.at(index).controls.cargoType.value)
  }

  cargoTypeIsBulk(index: number) {
    const list = Array.from(Object.values(HarbourDuesBulkCargoType)) as string[];

    return list.includes(this.list.at(index).controls.cargoType.value)
  }

  toggleCargoType(index: number) {
    if (!this.cargoTypeIsBulk(index)) {
      this.list.at(index).controls.goodsCodeType.disable();
      this.list.at(index).controls.goodsClassification.disable();
      this.list.at(index).controls.dangerInformation.disable();
    } else {
      this.list.at(index).controls.goodsCodeType.enable();
      this.list.at(index).controls.goodsClassification.enable();
      this.list.at(index).controls.dangerInformation.enable();
    }
  }

  protected readonly HarbourDuesCargoType = HarbourDuesCargoType;
  protected readonly Object = Object;
}
